// Considered: mounting `FilterGroupNodeMolecule` directly — rejected because
// every embedding host (uf-simple's "embedded tree", a related-spheres
// "narrowed to" block) needs the SAME quiet chrome around it: a light
// "Matching all/any of the following" bar, a Clear action and the empty-tree
// note, all deliberately quieter than the advanced dialog's navy pill (see
// advanced-filters-v2.jsx `UnifiedFilterTree`'s doc comment). Wrapping that
// chrome here once, instead of in every host, is the whole point of the
// component. See spec Reuse Audit.
import React, { useCallback } from 'react';
import { useTranslation } from 'localization';
import { isPropertyLeaf } from '@center-platform/spheres/filters';
import type { FilterGroup, FilterGroupLeaf } from 'types/filters';
import type { OrgAvailableTypeSchema } from 'types/schema';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import FilterGroupNodeMolecule from '../FilterGroupNode/FilterGroupNodeMolecule';
import { PropertyOption, PropertyType } from '../PropertyFilter/PropertyFilterMolecule';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { RelationshipOption } from '../RelationshipFilter/RelationshipFilterMolecule';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import { nextFilterId } from '../../../utils/ids';
import { readsAsPredicate } from '../../../utils/cfGroup';
import { normalizeBetweenInGroup } from '../../../utils/filterOperators';
import { canMoveInto, moveNode, addLeafToGroup, listGroupIds } from '../../../utils/filterTreeMove';
import { useLiftAndMove } from '../../../hooks/useLiftAndMove';
import './UnifiedFilterTreeMolecule.scss';

export interface UnifiedFilterTreeMoleculeProps {
  /** The canonical root group — `undefined` seeds an empty AND group. */
  value?: FilterGroup;
  onChange: (group: FilterGroup) => void;
  availableProperties: PropertyOption[];
  excludePropertyTypes?: PropertyType[];
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  scopeTypeLabels?: string[];
  arcTemplates?: ArcTemplateOption[];
  availableRelationships?: RelationshipOption[];
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  availablePerspectives?: PerspectiveOption[];
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /** False on a host whose evaluator cannot resolve a nested group. */
  allowGroups?: boolean;
  /**
   * Forwarded to every `perspective` lane leaf in the tree — locks that leaf's
   * perspective picker and starts it in Specific mode. Set by a
   * `focusPerspective` column host (`LevelFilterPopoverOrganism`); every other
   * host omits it. See `FilterGroupNodeMolecule`'s own `lockPerspective`.
   */
  lockPerspective?: boolean;
  /**
   * Forwarded to every `arc` lane leaf in the tree — locks that leaf's fused
   * arc-selector chrome. Set by a `focusArc` column host
   * (`LevelFilterPopoverOrganism`); every other host omits it. See
   * `FilterGroupNodeMolecule`'s own `lockArc`.
   */
  lockArc?: boolean;
  /**
   * Hides every property leaf's editor-footer Apply button (OQ-7) — set by a
   * column host (`LevelFilterPopoverOrganism`, `isColumnPopover`) whose
   * changes auto-commit (BLOCKER 1) instead of holding an "applied · unsaved"
   * edit for a separate Apply click. Forwarded to `FilterGroupNodeMolecule`.
   */
  hideApplyButton?: boolean;
  /**
   * `true` (default) draws the card chrome (header bar + Clear + border);
   * `false` returns the bare root group — no left rail, no sample panel, no
   * apply bar — for a host that mounts the tree inside its own chrome
   * (advanced-filters-v2.jsx's bare-embed case).
   */
  framed?: boolean;
  /** Show the "Reads as <subject> where …" plain-language footer. */
  readsAs?: boolean;
  /** Subject noun for the `readsAs` sentence — "Spheres" by default. */
  subject?: string;
  /** Inline variant (e.g. a segment panel) rather than a full card: a smaller corner radius and a small all/any bar control. */
  dense?: boolean;
  /**
   * Draws the "Matching all/any of the following" bar even for a flat list
   * (rows, no nested groups). Off by default, where the second row's joiner is
   * the flat-mode control; a host that draws the bar as designed opts in.
   */
  alwaysShowBar?: boolean;
  /** Overrides the default "no filters yet" note. */
  emptyNote?: React.ReactNode;
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
  className?: string;
  /**
   * Parity instrumentation only — overrides the framed root's default
   * `${datumScope}.card` id. The prototype's D1 artboard mounts this same
   * component four times as four DISTINCT contract slots (`uf-simple.card`,
   * `.card#2`, `.card#4`, `.card#5`); each dedicated parity story stands in
   * for one slot, so it must self-tag that slot's id, not the default's.
   * Omitted everywhere except those stories.
   */
  cardDatumId?: string;
  /**
   * Parity instrumentation — forwarded to `FilterGroupNodeMolecule`'s own
   * `datumScope`/`rootTabsDatumId`, and to this component's own framed-bar
   * `.toggle#2`/`.clear` ids. Defaults to the framed uf-simple embed's ids;
   * the advanced dialog mounts this bare (`framed={false}`) under its own
   * `uf-advanced` block, and the column adapter's session tier mounts the
   * SAME tree a second time under `uf-column` (`sessionTree=yes` in the
   * contract — literally the same `UnifiedFilterTree` component, a second
   * host).
   */
  datumScope?: string;
  rootTabsDatumId?: string;
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  nestedToggleDatumId?: string;
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  nestedRemoveDatumId?: string;
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  nestedJoinerLabelDatumId?: string;
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  nestedJoinerControlDatumId?: string;
  /**
   * Parity instrumentation — overrides the framed card's `data-container`
   * (defaults to `'ui-tree'`) and is forwarded to `FilterGroupNodeMolecule`'s
   * own `container`. The column adapter's session tier tags this `'ui-column'`.
   */
  container?: string;
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  focusedGroupId?: string;
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  onFocusGroup?: (groupId: string) => void;
  /** Forwarded to `FilterGroupNodeMolecule` — see that prop's own doc comment. */
  onValueFocus?: (filter: import('types/filters').FilterExpression) => void;
  /** No edit/remove/add/clear affordances anywhere in the tree. */
  readOnly?: boolean;
  /**
   * `'wide'` (default) keeps every existing host's current chrome — the
   * joiner gutter beside each row, a bordered card-in-card for nested
   * groups. `'narrow'` is the column/popover host: the joiner moves above
   * each row, nested groups collapse to a depth-rule + compact all/any
   * chip, and group nesting caps at depth 1 (`FilterGroupNodeMolecule`'s
   * own `narrow` prop — this is the host-level switch that sets it).
   */
  layout?: 'wide' | 'narrow';
  /**
   * Whether the narrow layout caps group nesting at depth 1 (default `true`).
   * A column/popover host caps it and points the author at the Advanced
   * editor for anything deeper ("Deeper nesting — use Advanced" replaces "Add
   * group", and drag-to-group refuses a deeper target). The Advanced editor IS
   * that destination, so it passes `false`: every depth keeps "Add group", as
   * the prototype's Advanced tree does — never a hint sending the author to
   * the surface they are already in. Ignored on a wide layout, which never caps.
   */
  capNarrowNesting?: boolean;
  /**
   * Opt-in drag-to-group (advanced-filters-v2.jsx `AFDnD`). Off by default —
   * a host without `dnd` renders no grips and no drop targets. This
   * component owns the actual drag state (which node is being dragged) and
   * commits drops via `filterTreeMove`'s `moveNode`; `FilterGroupNodeMolecule`
   * only renders the affordances.
   */
  dnd?: boolean;
  /**
   * Host-supplied per-leaf problem (a contradiction with a locked tier),
   * keyed by the leaf's own id. Forwarded to every `FilterGroupNodeMolecule`
   * at every depth.
   */
  problemOf?: (leafId: string) => { title: string; message: string } | undefined;
  /**
   * Every property row opens straight to its value instead of the closed
   * summary — the context editor's flat lists (`ContextLevelInspectorOrganism`,
   * `SegmentPanelMolecule`, `ApplicabilityEditorMolecule`) are edited
   * immediately, unlike the advanced dialog's accordion-of-closed-rows
   * default. Forwarded to every `FilterGroupNodeMolecule` at every depth.
   */
  defaultOpen?: boolean;
  /**
   * Fixes the ROOT group's operator for a host whose storage has no choice of conjunction —
   * e.g. a flat `propertyFilters` list, which is always an AND. When set, the root reads and
   * emits this operator whatever `value` carries, and no root all/any control renders: not
   * the framed "Matching all/any" bar (empty state included), not the root header's toggle,
   * and not the flat list's joiner control (it renders as the read-only echo). Combined with
   * `allowGroups={false}` the tree edits a flat list. Omitted (the default), every existing
   * host behaves as before.
   */
  fixedOperator?: 'and' | 'or';
}

const emptyRoot = (): FilterGroup => ({ id: nextFilterId(), operator: 'and', expressions: [] });

/**
 * A bare, embeddable filter tree — what a host surface mounts when it wants
 * the real builder inside its own chrome. `AdvancedFilterEditorOrganism`'s
 * centre pane is this molecule (`framed=false`, `allowGroups=true`); the
 * simple-mode embed (uf-simple) is this molecule framed.
 */
const UnifiedFilterTreeMolecule: React.FC<UnifiedFilterTreeMoleculeProps> = ({
  value,
  onChange: onChangeProp,
  availableProperties,
  excludePropertyTypes,
  scopeTypeLabels,
  arcTemplates,
  availableRelationships,
  familyTypeSchemas,
  availablePerspectives,
  perspectiveSearchProps,
  allowGroups = true,
  lockPerspective = false,
  lockArc = false,
  hideApplyButton = false,
  framed = true,
  readsAs = true,
  subject,
  dense = false,
  alwaysShowBar = false,
  emptyNote,
  renderCatalog,
  className = '',
  cardDatumId,
  datumScope = 'uf-simple',
  rootTabsDatumId,
  nestedToggleDatumId,
  nestedRemoveDatumId,
  nestedJoinerLabelDatumId,
  nestedJoinerControlDatumId,
  container = 'ui-tree',
  focusedGroupId,
  onFocusGroup,
  onValueFocus,
  readOnly = false,
  layout = 'wide',
  capNarrowNesting = true,
  dnd = false,
  problemOf,
  defaultOpen = false,
  fixedOperator,
}) => {
  const { t, i18n } = useTranslation();
  // Every host's stored filters enter the editor here, so this is where a row
  // persisted in a legacy shape is read canonically: a `between` still holding
  // its range as `value: [from, to]` (which no engine reads) becomes `min`/
  // `max`. Read-only normalisation — the host's value is untouched until the
  // author's next edit, whose `onChange` then carries the canonical row.
  const root = React.useMemo(() => {
    const normalized = normalizeBetweenInGroup(value ?? emptyRoot());
    return fixedOperator ? { ...normalized, operator: fixedOperator } : normalized;
  }, [value, fixedOperator]);
  // Every emitted root carries the fixed operator, whichever path produced it.
  const onChange = useCallback(
    (next: FilterGroup) => onChangeProp(fixedOperator ? { ...next, operator: fixedOperator } : next),
    [onChangeProp, fixedOperator]
  );
  const empty = (root.expressions?.length ?? 0) === 0 && (root.groups?.length ?? 0) === 0;
  const narrow = layout === 'narrow';
  // Drag-to-group honours the same depth cap "Add group" does.
  const moveOptions = React.useMemo(() => ({ narrow: narrow && capNarrowNesting }), [narrow, capNarrowNesting]);
  // A read-only tree offers no drag affordances at all — no grips, no drop
  // targets, no keyboard lift — and refuses a move even if one is attempted.
  const dndActive = dnd && !readOnly;
  // Flat mode (advanced-filters-v2.jsx `AFTree`): a tree with rows but no
  // nested groups draws no "Matching all/any" bar at all — the flat-mode
  // control is the second row's own joiner (`FilterJoinerAtom`'s `onSetOp`),
  // not a separate header. A group appearing brings the bar straight back.
  // An empty root is flat too — it has no group to combine, so the bar has
  // nothing to control; the card's own empty-tree note (below) is its only
  // "nothing here" message, not the group node's redundant one.
  const flat = (root.groups?.length ?? 0) === 0;

  const setOperator = useCallback(
    (operator: 'and' | 'or') => {
      if (readOnly) return;
      onChange({ ...root, operator });
    },
    [root, onChange, readOnly]
  );

  const clear = useCallback(() => {
    if (readOnly) return;
    onChange({ id: root.id, operator: root.operator, expressions: [] });
  }, [root, onChange, readOnly]);

  const [dragNodeId, setDragNodeId] = React.useState<string | null>(null);
  const handleDragStartNode = useCallback((nodeId: string) => setDragNodeId(nodeId), []);
  const handleDragEndNode = useCallback(() => setDragNodeId(null), []);
  const handleCanDropNode = useCallback(
    (nodeId: string, targetGroupId: string) =>
      dndActive && canMoveInto(root, nodeId, targetGroupId, moveOptions),
    [dndActive, root, moveOptions]
  );
  // A drop makes its target group the add target (spec § Drag and drop: "the
  // target group becomes the add target … after the drop"), so the author's
  // next "Add condition" from the host lands beside what they just moved.
  // One path for both the pointer drop and the keyboard drop (`handleLiftEnd`).
  const handleDropNode = useCallback(
    (nodeId: string, targetGroupId: string) => {
      if (!dndActive || !canMoveInto(root, nodeId, targetGroupId, moveOptions)) return;
      onChange(moveNode(root, nodeId, targetGroupId, moveOptions));
      onFocusGroup?.(targetGroupId);
    },
    [dndActive, root, moveOptions, onChange, onFocusGroup]
  );

  // Keyboard drag-to-group (advanced-filters-v2.jsx `AFDnD`'s keyboard path): the SAME
  // lift/cancel/announce mechanics as `ReorderableListMolecule`'s column reorder, in
  // `'preview'` mode — Arrow keys move a target-group cursor with no tree mutation; only
  // Enter commits, once, through `moveNode`. There is exactly one lift at a time in this
  // tree and only its own grip is ever focused meanwhile, so the hook's identity plumbing
  // (`keyOf`) can be a constant — no per-position identity to track.
  const groupIds = React.useMemo(() => listGroupIds(root), [root]);
  const handleLiftEnd = useCallback(
    (_from: string, to: string) => {
      if (dragNodeId) handleDropNode(dragNodeId, to);
    },
    [dragNodeId, handleDropNode]
  );
  const handleLiftSettled = useCallback(() => setDragNodeId(null), []);
  const canMoveLift = useCallback(
    (_from: string, to: string) => !!dragNodeId && canMoveInto(root, dragNodeId, to, moveOptions),
    [dragNodeId, root, moveOptions]
  );
  const {
    liftedPos: keyboardCursorGroupId,
    announcement: liftAnnouncement,
    handleKeyDown: handleGripKeyDown,
    handleBlur: handleGripBlur,
  } = useLiftAndMove<string>({
    positions: groupIds,
    keyOf: () => 'lift',
    isLiftable: () => true,
    canMove: canMoveLift,
    commitMode: 'preview',
    onLiftEnd: handleLiftEnd,
    onLiftSettled: handleLiftSettled,
    disabled: !dndActive,
    labels: {
      liftedAnnouncement: () => t('filters.tree.dragLifted'),
      movedAnnouncement: (index, total) => t('filters.tree.dragMoved', { index: index + 1, total }),
      droppedAnnouncement: (index, total) => t('filters.tree.dragDropped', { index: index + 1, total }),
      cancelledAnnouncement: t('filters.tree.dragCancelled'),
    },
  });
  const handleGripKeyDownWithLiftStart = useCallback(
    (e: React.KeyboardEvent, nodeId: string, currentGroupId: string) => {
      if (dndActive && !dragNodeId && (e.key === ' ' || e.key === 'Enter')) {
        handleDragStartNode(nodeId);
      }
      handleGripKeyDown(e, currentGroupId);
    },
    [dndActive, dragNodeId, handleDragStartNode, handleGripKeyDown]
  );

  // Narrow hosts drill the "Add condition" catalog in below the tree itself,
  // anchored to whichever group last requested it, instead of each
  // `FilterGroupNodeMolecule` floating its own popover — a narrow host has no
  // room to clip a floating shell against (EAINT-908).
  const [drillInGroupId, setDrillInGroupId] = React.useState<string | null>(null);
  const handleRequestAddCondition = useCallback((groupId: string) => setDrillInGroupId(groupId), []);
  // The id of the leaf most recently added — either through this drill-in or
  // a node's own floating catalog (`onLeafAdded`, forwarded from
  // `FilterGroupNodeMolecule`) — so the matching row opens straight to its
  // value instead of the closed summary (EAINT-931 pickingAnAspectAddsARowOpenOnItsValue).
  const [justAddedLeafId, setJustAddedLeafId] = React.useState<string | undefined>(undefined);
  const handleDrillInPick = useCallback(
    (leaf: FilterGroupLeaf) => {
      if (drillInGroupId) {
        onChange(addLeafToGroup(root, drillInGroupId, leaf));
        const newLeafId = isPropertyLeaf(leaf) ? leaf.id : leaf.filter.id;
        if (newLeafId) setJustAddedLeafId(newLeafId);
      }
      setDrillInGroupId(null);
    },
    [drillInGroupId, root, onChange]
  );
  const handleDrillInClose = useCallback(() => setDrillInGroupId(null), []);

  // The footer sentence (advanced-filters-v2.jsx `UnifiedFilterTree`'s "Reads
  // as" readout) — in the reader's words, through the same catalogs the rows
  // use. Only complete conditions constrain, so a tree whose rows are all
  // still being authored says it narrows nothing yet rather than going blank.
  const readsAsText = React.useMemo(() => {
    if (!readsAs || empty) return '';
    const subjectText = subject ?? t('filters.tree.defaultSubject');
    const predicate = readsAsPredicate(root, {
      t,
      availableProperties,
      availableRelationships,
      availablePerspectives,
      locale: i18n?.language,
    });
    return predicate
      ? t('filters.tree.readsAsSentence', { subject: subjectText, predicate })
      : t('filters.tree.readsAsUnnarrowed', { subject: subjectText });
  }, [readsAs, empty, subject, t, root, availableProperties, availableRelationships, availablePerspectives, i18n?.language]);

  const body = (
    <FilterGroupNodeMolecule
      group={root}
      isRoot
      hideHeader={framed || flat || !!fixedOperator}
      operatorFixed={!!fixedOperator}
      allowGroups={allowGroups}
      lockPerspective={lockPerspective}
      lockArc={lockArc}
      hideApplyButton={hideApplyButton}
      availableProperties={availableProperties}
      excludePropertyTypes={excludePropertyTypes}
      scopeTypeLabels={scopeTypeLabels}
      arcTemplates={arcTemplates}
      availableRelationships={availableRelationships}
      familyTypeSchemas={familyTypeSchemas}
      availablePerspectives={availablePerspectives}
      perspectiveSearchProps={perspectiveSearchProps}
      renderCatalog={renderCatalog}
      datumScope={datumScope}
      container={container}
      rootTabsDatumId={rootTabsDatumId}
      nestedToggleDatumId={nestedToggleDatumId}
      nestedRemoveDatumId={nestedRemoveDatumId}
      nestedJoinerLabelDatumId={nestedJoinerLabelDatumId}
      nestedJoinerControlDatumId={nestedJoinerControlDatumId}
      focusedGroupId={focusedGroupId}
      onFocusGroup={onFocusGroup}
      onChange={onChange}
      readOnly={readOnly}
      narrow={narrow}
      capNarrowNesting={capNarrowNesting}
      dnd={dndActive}
      dragNodeId={dragNodeId}
      onDragStartNode={handleDragStartNode}
      onDragEndNode={handleDragEndNode}
      canDropNode={handleCanDropNode}
      onDropNode={handleDropNode}
      keyboardCursorGroupId={keyboardCursorGroupId}
      onGripKeyDown={handleGripKeyDownWithLiftStart}
      onGripBlur={() => handleGripBlur('lift')}
      onRequestAddCondition={narrow ? handleRequestAddCondition : undefined}
      problemOf={problemOf}
      justAddedLeafId={justAddedLeafId}
      onLeafAdded={setJustAddedLeafId}
      defaultOpen={defaultOpen}
      onValueFocus={onValueFocus}
    />
  );

  const dragAnnouncer = dndActive && (
    <div className="unified-filter-tree-molecule__announcer" aria-live="polite">
      {liftAnnouncement}
    </div>
  );

  const drillIn = narrow && drillInGroupId && renderCatalog && (
    <div
      className="unified-filter-tree-molecule__drill-in"
      data-datum={`${datumScope}.drill-in`}
      data-container={container}
    >
      <div className="unified-filter-tree-molecule__drill-in-header">
        <ButtonAtom
          type="button"
          label={t('filters.tree.back')}
          icon="pi pi-chevron-left"
          variant="text"
          size="small"
          onClick={handleDrillInClose}
          data-datum={`${datumScope}.back`}
        />
        <span className="unified-filter-tree-molecule__drill-in-title">
          {t('filters.tree.addConditionTitle')}
        </span>
        <span className="unified-filter-tree-molecule__spacer" />
        <span className="unified-filter-tree-molecule__drill-in-target">
          {drillInGroupId === root.id
            ? t('filters.tree.addConditionToFilter')
            : t('filters.tree.addConditionToGroup')}
        </span>
      </div>
      {renderCatalog({ onPick: handleDrillInPick, onClose: handleDrillInClose })}
    </div>
  );

  if (!framed) return (
    <>
      {dragAnnouncer}
      {body}
      {drillIn}
    </>
  );

  let operatorBarSize: 'compact' | 'small' | 'normal' = 'normal';
  if (alwaysShowBar) operatorBarSize = 'compact';
  else if (dense) operatorBarSize = 'small';

  return (
    <div
      className={`unified-filter-tree-molecule${dense ? ' unified-filter-tree-molecule--dense' : ''} ${className}`.trim()}
      data-datum={cardDatumId ?? `${datumScope}.card`}
      data-kind="card"
      data-container={container}
    >
      {dragAnnouncer}
      {(!flat || (alwaysShowBar && !empty)) && !fixedOperator && (
        <div
          className={`unified-filter-tree-molecule__bar${alwaysShowBar ? ' unified-filter-tree-molecule__bar--quiet' : ''}`}
        >
          <span className="unified-filter-tree-molecule__matching-label">
            {t('filters.tree.matching')}
          </span>
          <SegmentedControlAtom
            mode="single"
            size={operatorBarSize}
            tone={alwaysShowBar ? 'quiet' : 'default'}
            options={[
              { value: 'and', label: t('filters.filterGroups.matchAll') },
              { value: 'or', label: t('filters.filterGroups.matchAny') },
            ]}
            value={root.operator}
            onChange={(v) => setOperator(v as 'and' | 'or')}
            ariaLabel={t('filters.filterGroups.operatorLabel')}
            data-datum={`${datumScope}.toggle#2`}
          />
          <span className="unified-filter-tree-molecule__of-label">
            {t('filters.filterGroups.ofFollowing')}
          </span>
          <span className="unified-filter-tree-molecule__spacer" />
          {!readOnly && (
            // uf-simple.clear (EAINT-936) — advanced-filters-v2.jsx:1127's quiet
            // `.btn-reset` "Clear" text trigger. ButtonAtom's `text` variant carries a
            // 40px height and 8px horizontal padding meant for a labelled action button;
            // this is an unstyled inline text link, so it's a native button here, matching
            // the same `.btn-reset` precedent as `ArcFilterMolecule`'s `&__clear-btn` and
            // `ArcValueEditorMolecule`'s `&__advanced-toggle`. Colour stays `--text-color-muted`
            // rather than the prototype's lighter slate — same WCAG-contrast trade-off as
            // OQ-14/OQ-18 (see OQ-20).
            <button
              type="button"
              className="unified-filter-tree-molecule__clear-btn"
              onClick={clear}
              data-datum={`${datumScope}.clear`}
            >
              {t('filters.tree.clear')}
            </button>
          )}
        </div>
      )}
      <div className="unified-filter-tree-molecule__body">
        {empty && (
          <div className="unified-filter-tree-molecule__empty">
            {emptyNote ?? t('filters.tree.emptyDefault')}
          </div>
        )}
        {body}
        {drillIn}
      </div>
      {readsAs && !empty && (
        <div className="unified-filter-tree-molecule__reads-as">
          <span className="unified-filter-tree-molecule__reads-as-label">
            {t('filters.tree.readsAsLabel')}
          </span>
          <span className="unified-filter-tree-molecule__reads-as-text">{readsAsText}</span>
        </div>
      )}
    </div>
  );
};

UnifiedFilterTreeMolecule.displayName = 'UnifiedFilterTreeMolecule';

export default UnifiedFilterTreeMolecule;
